import React, { useState } from "react";
import { Container, Row, Col, Spinner, Modal, ModalBody } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import antivirusService from "../../services/antivirusService";

import ActiveScansOverview from "./ActiveScansOverview";
import ScheduledScans from "./ScheduledScans";
import ScanHistory from "./ScanHistory";
import ProtectionStatusCard from "./ProtectionStatusCard";

const AntivirusDashboard = () => {
  document.title = "Antivirus Management - RiskNoX";

  const headingIconChip = {
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: "1px solid rgba(var(--vz-success-rgb), 0.35)",
  background: "rgba(13, 48, 3, 0.12)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

  const [loadingType, setLoadingType] = useState(null);
  const [scanModal, setScanModal] = useState({ open: false, type: null, success: false, error: null });

  const handleTriggerScan = async (type) => {
    try {
      setLoadingType(type);
      const res = await antivirusService.triggerGlobalScan(`${type}scan`);
      setScanModal({ open: true, type, success: true, error: null, message: res.message });
    } catch (err) {
      setScanModal({ open: true, type, success: false, error: err.message });
    } finally {
      setLoadingType(null);
    }
  };

  const closeScanModal = () => setScanModal({ open: false, type: null, success: false, error: null });
  const isQuick = scanModal.type === "quick";

  return (
    <div className="page-content">
      <Container fluid>

        {/* ── PAGE HEADER ── */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <h4 style={{ fontWeight: 700, color: "var(--vz-body-color)", margin: "0 0 4px" }}>
                <span style={headingIconChip} className="me-2">
                <FeatherIcon icon="shield" size={18} style={{ color: "var(--vz-success)",  verticalAlign: "middle" }} />
                </span>
                Antivirus Protection
              </h4>
              <p style={{ color: "var(--vz-secondary-color)", fontSize: 13, margin: 0 }}>
                Real-time threat detection and endpoint protection across your network
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => handleTriggerScan("quick")}
                disabled={!!loadingType}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: "rgba(var(--vz-info-rgb),.1)",
                  border: "1px solid rgba(var(--vz-info-rgb),.35)",
                  color: "var(--vz-info)", cursor: "pointer",
                  opacity: loadingType ? 0.7 : 1,
                }}
              >
                {loadingType === "quick"
                  ? <><Spinner size="sm" className="me-1" />Initiating...</>
                  : <><FeatherIcon icon="zap" size={14} />Quick Scan</>}
              </button>
              <button
                onClick={() => handleTriggerScan("full")}
                disabled={!!loadingType}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: "var(--vz-success)", border: "none",
                  color: "#fff", cursor: "pointer",
                  opacity: loadingType ? 0.7 : 1,
                  boxShadow: "0 4px 12px rgba(var(--vz-success-rgb),.3)"
                }}
              >
                {loadingType === "full"
                  ? <><Spinner size="sm" className="me-1" />Initiating...</>
                  : <><FeatherIcon icon="shield" size={14} />Full System Scan</>}
              </button>
            </div>
          </div>
        </div>

        {/* ── ROW 1: Stats + Protection ── */}
        <Row className="g-3 mb-3">
          <Col xl={8}><ActiveScansOverview /></Col>
          <Col xl={4}><ProtectionStatusCard /></Col>
        </Row>

        {/* ── ROW 2: Scheduled Scans ── */}
        <Row className="g-3 mb-3">
          <Col xl={12}><ScheduledScans /></Col>
        </Row>

        {/* ── ROW 3: Scan History ── */}
        <Row className="g-3">
          <Col xl={12}><ScanHistory /></Col>
        </Row>

      </Container>

      {/* ── SCAN MODAL ── */}
      <Modal isOpen={scanModal.open} toggle={closeScanModal} centered size="sm">
        <div style={{
          background: "var(--vz-secondary-bg, #fff)",
          border: "1px solid var(--vz-border-color)",
          borderRadius: 12, overflow: "hidden"
        }}>
          <ModalBody className="p-4 text-center">
            {scanModal.success ? (
              <>
                <div className="mx-auto mb-3 d-flex align-items-center justify-content-center" style={{
                  width: 64, height: 64, borderRadius: "50%",
                  background: isQuick ? "rgba(var(--vz-info-rgb),.1)" : "rgba(var(--vz-success-rgb),.1)",
                  border: `2px solid ${isQuick ? "rgba(var(--vz-info-rgb),.3)" : "rgba(var(--vz-success-rgb),.3)"}`,
                  color: isQuick ? "var(--vz-info)" : "var(--vz-success)"
                }}>
                  <FeatherIcon icon={isQuick ? "zap" : "shield"} size={28} />
                </div>
                <h5 style={{ fontWeight: 700, color: "var(--vz-body-color)", marginBottom: 8 }}>
                  {isQuick ? "Quick Scan Initiated" : "Full System Scan Initiated"}
                </h5>
                <p style={{ color: "var(--vz-secondary-color)", fontSize: 13, marginBottom: 16 }}>
                  {isQuick
                    ? "A quick scan has been queued for all active endpoints. It will execute within 1 minute."
                    : "A full system scan has been queued. This may take 15–60 minutes depending on endpoint size."}
                </p>
                <div className="p-3 rounded-3 text-start mb-4" style={{
                  background: "rgba(var(--vz-primary-rgb),.04)",
                  border: "1px solid rgba(var(--vz-primary-rgb),.15)"
                }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FeatherIcon icon="info" size={13} style={{ color: "var(--vz-info)" }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--vz-body-color)" }}>What happens next?</span>
                  </div>
                  <ul style={{ fontSize: 12, color: "var(--vz-secondary-color)", margin: 0, paddingLeft: 18 }}>
                    <li className="mb-1">Scan command queued and picked up within 1 minute</li>
                    <li className="mb-1">Results collected automatically from managed endpoints</li>
                    <li>Threats will appear in scan history if detected</li>
                  </ul>
                </div>
                <button onClick={closeScanModal} style={{
                  width: "100%", padding: "10px 0", borderRadius: 8, fontWeight: 600,
                  fontSize: 13, cursor: "pointer",
                  background: isQuick ? "rgba(var(--vz-info-rgb),.1)" : "var(--vz-success)",
                  border: isQuick ? "1px solid rgba(var(--vz-info-rgb),.3)" : "none",
                  color: isQuick ? "var(--vz-info)" : "#fff"
                }}>
                  Got it
                </button>
              </>
            ) : (
              <>
                <div className="mx-auto mb-3 d-flex align-items-center justify-content-center" style={{
                  width: 64, height: 64, borderRadius: "50%",
                  background: "rgba(var(--vz-danger-rgb),.1)",
                  border: "2px solid rgba(var(--vz-danger-rgb),.3)",
                  color: "var(--vz-danger)"
                }}>
                  <FeatherIcon icon="alert-circle" size={28} />
                </div>
                <h5 style={{ fontWeight: 700, color: "var(--vz-body-color)", marginBottom: 8 }}>Scan Failed</h5>
                <p style={{ color: "var(--vz-secondary-color)", fontSize: 13, marginBottom: 20 }}>
                  {scanModal.error || "Failed to initiate scan. Please try again."}
                </p>
                <button onClick={closeScanModal} style={{
                  width: "100%", padding: "10px 0", borderRadius: 8, fontWeight: 600,
                  fontSize: 13, cursor: "pointer",
                  background: "rgba(var(--vz-danger-rgb),.1)",
                  border: "1px solid rgba(var(--vz-danger-rgb),.3)",
                  color: "var(--vz-danger)"
                }}>
                  Close
                </button>
              </>
            )}
          </ModalBody>
        </div>
      </Modal>
    </div>
  );
};

export default AntivirusDashboard;
